import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import { Direction, ValidationSeverity } from '@k-ucap/shared';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useIntegrationStore } from '@/stores/useIntegrationStore.js';
import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';
import type { MessageEvent, SimulationRun, MessageInstance } from '@k-ucap/shared';

const PAGE_SIZE = 100;

const eventToInstance = (event: MessageEvent): MessageInstance => ({
  id: event.id,
  messageType: event.messageType,
  standard: event.standard,
  name: event.name,
  source: event.source,
  target: event.target,
  direction: event.direction,
  timestamp: event.timestamp,
  fields: event.fields,
  rawBytes: event.rawBytes,
  validation: event.validation,
  projectId: event.projectId,
  environment: event.environment,
  sequenceNumber: event.sequenceNumber,
  runId: event.runId,
  messageDefinitionId: event.messageDefinitionId,
});

export const MessagesPage = () => {
  const navigate = useNavigate();
  const { messageEventRepository, simulationRunRepository } = useRepositories();
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const [events, setEvents] = useState<MessageEvent[]>([]);
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [directionFilter, setDirectionFilter] = useState<string>('');
  const [resultFilter, setResultFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sourceRoleFilter, setSourceRoleFilter] = useState<string>('');
  const [targetRoleFilter, setTargetRoleFilter] = useState<string>('');
  const [runFilter, setRunFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [directionFilter, resultFilter, typeFilter, sourceRoleFilter, targetRoleFilter, runFilter]);

  useEffect(() => {
    if (!selectedProject) {
      setEvents([]);
      setRuns([]);
      return;
    }
    const unsubscribe = messageEventRepository.subscribeToProject?.(selectedProject.id, (loaded) => {
      setEvents(loaded);
    });
    simulationRunRepository
      .findByProjectId(selectedProject.id)
      .then((loaded) => setRuns(loaded))
      .catch(() => setRuns([]));
    if (unsubscribe) {
      return () => unsubscribe();
    }
    messageEventRepository
      .findByProjectId(selectedProject.id)
      .then((loaded) => setEvents(loaded))
      .catch(() => setEvents([]));
    return;
  }, [messageEventRepository, simulationRunRepository, selectedProject]);

  const filtered = useMemo(() => {
    return events.filter((m) => {
      const dirOk = directionFilter ? m.direction === directionFilter : true;
      const resOk = resultFilter ? m.validation === resultFilter : true;
      const typeOk = typeFilter ? String(m.messageType) === typeFilter : true;
      const sourceOk = sourceRoleFilter ? m.sourceRole === sourceRoleFilter : true;
      const targetOk = targetRoleFilter ? m.targetRole === targetRoleFilter : true;
      const runOk = runFilter ? m.runId === runFilter : true;
      return dirOk && resOk && typeOk && sourceOk && targetOk && runOk;
    });
  }, [events, directionFilter, resultFilter, typeFilter, sourceRoleFilter, targetRoleFilter, runFilter]);

  const paginated = useMemo(() => filtered.slice(0, page * PAGE_SIZE), [filtered, page]);
  const hasMore = paginated.length < filtered.length;

  const messageTypes = useMemo(() => Array.from(new Set(events.map((e) => String(e.messageType)))).sort(), [events]);
  const sourceRoles = useMemo(
    () => Array.from(new Set(events.map((e) => e.sourceRole).filter(Boolean))).sort(),
    [events]
  );
  const targetRoles = useMemo(
    () => Array.from(new Set(events.map((e) => e.targetRole).filter(Boolean))).sort(),
    [events]
  );

  const handleExport = (format: 'json' | 'jsonl' | 'csv' | 'hex') => {
    const data = paginated;
    let content = '';
    let mimeType = 'text/plain';
    let extension = 'txt';
    if (format === 'json') {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
      extension = 'json';
    } else if (format === 'jsonl') {
      content = data.map((e) => JSON.stringify(e)).join('\n');
      mimeType = 'application/jsonlines';
      extension = 'jsonl';
    } else if (format === 'csv') {
      const headers = [
        'timestamp',
        'direction',
        'messageType',
        'name',
        'source',
        'target',
        'validation',
        'sequenceNumber',
      ];
      const rows = data.map((e) =>
        [
          e.timestamp,
          e.direction,
          e.messageType,
          e.name,
          e.source,
          e.target,
          e.validation ?? '',
          e.sequenceNumber ?? '',
        ].join(',')
      );
      content = [headers.join(','), ...rows].join('\n');
      mimeType = 'text/csv';
      extension = 'csv';
    } else if (format === 'hex') {
      content = data
        .map(
          (e) =>
            `${e.id} ${e.timestamp} ${e.rawHex ?? e.rawBytes?.map((b) => b.toString(16).padStart(2, '0').toUpperCase()).join(' ') ?? ''}`
        )
        .join('\n');
      mimeType = 'text/plain';
      extension = 'txt';
    }
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `messages-${selectedProject?.id ?? 'export'}.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (file: File) => {
    setImportError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        const parsed: MessageEvent[] = [];
        if (text.trim().startsWith('[')) {
          const arr = JSON.parse(text) as MessageEvent[];
          parsed.push(...arr);
        } else if (text.includes('\n')) {
          const lines = text.split('\n').filter(Boolean);
          for (const line of lines) {
            try {
              parsed.push(JSON.parse(line) as MessageEvent);
            } catch {
              // skip invalid lines
            }
          }
        } else {
          parsed.push(JSON.parse(text) as MessageEvent);
        }
        const adapter = getGlobalVirtualMessageAdapter();
        parsed.forEach((event) => {
          adapter.publish(eventToInstance(event)).catch(() => {});
        });
        useIntegrationStore.getState().setImporting(false, null);
      } catch (err) {
        setImportError((err as Error).message);
        useIntegrationStore.getState().setImporting(false, (err as Error).message);
      }
    };
    reader.onerror = () => {
      setImportError('파일 읽기 실패');
    };
    useIntegrationStore.getState().setImporting(true, null);
    reader.readAsText(file);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        메시지 로그
      </Typography>
      {!selectedProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          프로젝트를 선택해야 메시지 로그를 조회할 수 있습니다.
        </Alert>
      )}
      {importError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
          {importError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Direction</InputLabel>
          <Select value={directionFilter} label="Direction" onChange={(e) => setDirectionFilter(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value={Direction.TX}>TX</MenuItem>
            <MenuItem value={Direction.RX}>RX</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Result</InputLabel>
          <Select value={resultFilter} label="Result" onChange={(e) => setResultFilter(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value={ValidationSeverity.PASS}>PASS</MenuItem>
            <MenuItem value={ValidationSeverity.WARN}>WARN</MenuItem>
            <MenuItem value={ValidationSeverity.FAIL}>FAIL</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Message Type</InputLabel>
          <Select value={typeFilter} label="Message Type" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            {messageTypes.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Source Role</InputLabel>
          <Select value={sourceRoleFilter} label="Source Role" onChange={(e) => setSourceRoleFilter(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            {sourceRoles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Target Role</InputLabel>
          <Select value={targetRoleFilter} label="Target Role" onChange={(e) => setTargetRoleFilter(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            {targetRoles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 240 }} size="small">
          <InputLabel>Run</InputLabel>
          <Select value={runFilter} label="Run" onChange={(e) => setRunFilter(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            {runs.map((run) => (
              <MenuItem key={run.id} value={run.id}>
                {run.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={() => handleExport('json')}>
          Export JSON
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleExport('jsonl')}>
          Export JSONL
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleExport('csv')}>
          Export CSV
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleExport('hex')}>
          Export HEX
        </Button>
        <Button size="small" variant="outlined" component="label">
          Import
          <input
            type="file"
            accept=".json,.jsonl,.csv,.txt"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImport(file);
              e.target.value = '';
            }}
          />
        </Button>
      </Box>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Message ID</TableCell>
              <TableCell>Message Name</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Seq</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((msg) => (
              <TableRow key={msg.id}>
                <TableCell>{new Date(msg.timestamp).toLocaleTimeString()}</TableCell>
                <TableCell>{msg.direction}</TableCell>
                <TableCell>{msg.messageType}</TableCell>
                <TableCell>{msg.name}</TableCell>
                <TableCell>{msg.source}</TableCell>
                <TableCell>{msg.target}</TableCell>
                <TableCell>
                  <Chip
                    label={msg.validation || 'N/A'}
                    size="small"
                    color={
                      msg.validation === ValidationSeverity.PASS
                        ? 'success'
                        : msg.validation === ValidationSeverity.WARN
                          ? 'warning'
                          : 'default'
                    }
                  />
                </TableCell>
                <TableCell>{msg.sequenceNumber ?? '-'}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/analyzer?messageEventId=${msg.id}`)}
                  >
                    Analyze
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={() => setPage((p) => p + 1)}>
            Load More ({filtered.length - paginated.length} remaining)
          </Button>
        </Box>
      )}
    </Box>
  );
};
